import { createReducer } from '@reduxjs/toolkit';
import _ from 'lodash';
import { randomInt } from '../../../reducers/util';
import { MAX_EVENT_NO } from '../DisplayEvents';
import { CohortPathway, DestinationFilter, EventConfig, EventFilter, PathwayFilter } from '../types/CohortPathway';

export interface PathwayState {
    pathwayId: number | undefined
    pathways: CohortPathway[]
    currentFilter: EventFilter | DestinationFilter | undefined
}

const initialState: PathwayState = {
    pathwayId: undefined,
    pathways: [],
    currentFilter: undefined
}

export function emptyPathwayFilter(): PathwayFilter {
    return {
        diagOr: false,
        primaryDiagnosis: [],
        secondaryDiagnosis: [],
        trusts: [],
        procedures: [],
        hrgs: [],
        treatments: [],
        subICBLocations: [],
        fromAge: undefined,
        toAge: undefined,
        gender: []
    }
}

export function emptyEventConfig(eventNo: number): EventConfig {
    return {
        datasets: [1, 2, 3, 4],
        eventNo: eventNo,
        eventFilter: undefined,
        destinationFilters: []
    }
}

export function emptyPathway(events: number = 2): CohortPathway {
    const eventConfigs = Array
        .from(Array(events).keys())
        .map(i => emptyEventConfig(i))
    return {
        id: randomInt(),
        createdOn: new Date().getTime(),
        title: 'default pathway title',
        eventConfigs: eventConfigs,
        eventMetrics: []
    }
}

function addPathway(state: PathwayState, pathway: CohortPathway) {
    state.pathways.unshift(pathway)
    state.pathwayId = pathway.id
}

const pathway = createReducer(
    initialState,
    {
        PATHWAY_NEW_PATHWAY: (state, action) => {
            const pathway = emptyPathway()
            state.pathways.unshift(pathway)
            state.pathwayId = pathway.id
        },
        PATHWAY_ADD_PATHWAY: (state, action) => {
            addPathway(state, action.pathway)
        },
        PATHWAY_SET_CURRENT_PATHWAY: (state, action) => {
            state.pathwayId = action.pathwayId
        },
        PATHWAY_SET_DEFAULT_PATHWAY: (state, action) => {
            if (action.query.pathwayStatus === 'COMPLETE') {
                if (state.pathways.length === 0) {
                    addPathway(state, emptyPathway())
                } else if (!_.isNil(action.exceptPathwayId)) {
                    const result = _.find(state.pathways, (i) => i.id !== action.exceptPathwayId)
                    if (_.isNil(result)) {
                        addPathway(state, emptyPathway())
                    } else {
                        state.pathwayId = result.id
                    }
                } else {
                    state.pathwayId = state.pathways[0].id
                }
            } else {
                // pathways for this cohort need to generated
            }
        },
        PATHWAY_SET_NUMBER_OF_EVENTS: (state, action) => {
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            const noOfEvents = action.noOfEvents
            if (pathway.eventConfigs.length > noOfEvents) {
                _.remove(pathway.eventConfigs, (i, j) => j >= noOfEvents)
            } else {
                let i = pathway.eventConfigs.length
                for (; i < noOfEvents; i++) {
                    pathway.eventConfigs[i] = emptyEventConfig(i)
                }
            }
        },
        PATHWAY_INCREMENT_EVENTS: (state, action) => {
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            if (pathway.eventConfigs.length <= MAX_EVENT_NO) {
                pathway.eventConfigs.push(emptyEventConfig(pathway.eventConfigs.length))
            }
        },
        PATHWAY_DECREMENT_EVENTS: (state, action) => {
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            pathway.eventConfigs.splice(-1, 1)
        },
        PATHWAY_UPDATE_PATHWAY: (state, action) => {
            const index = _.findIndex(state.pathways, { id: state.pathwayId })
            state.pathways.splice(index, 1, action.pathway)
        },
        PATHWAY_SET_CURRENT_PATHWAY_TITLE: (state, action) => {
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            pathway.title = action.title
        },
        PATHWAY_FILTER_CLOSE: (state, action) => {
            state.currentFilter = undefined
        },
        PATHWAY_FILTER_ON_DESTINATION_METRIC: (state, action) => {
            const eventNo = action.destinationMetric.eventNo
            const dataset = action.destinationMetric.dataset

            const pathway = findPathway(state.pathways, state.pathwayId!)!
            const eventConfig = findEventConfig(pathway, eventNo)
            let destinationFilter = findDestinationFilter(eventConfig!, dataset)

            if (_.isNil(destinationFilter)) {
                destinationFilter = {
                    eventNo: eventNo,
                    dataset: dataset,
                    filter: emptyPathwayFilter()
                }
                eventConfig?.destinationFilters.push(destinationFilter)
            }

            state.currentFilter = destinationFilter as DestinationFilter
        },
        PATHWAY_UPDATE_FILTER_ON_DESTINATION_METRIC: (state, action) => {
            const eventNo = action.destinationFilter.eventNo
            const dataset = action.destinationFilter.dataset
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            const eventConfig = findEventConfig(pathway, eventNo)
            const index = _.findIndex(eventConfig!.destinationFilters, j => j.dataset === dataset)
            eventConfig!.destinationFilters[index] = action.destinationFilter
        },
        PATHWAY_FILTER_ON_EVENT: (state, action) => {
            const eventNo = action.eventConfig.eventNo
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            const eventConfig = findEventConfig(pathway, eventNo)
            let eventFilter = eventConfig!.eventFilter

            if (_.isNil(eventFilter)) {
                eventFilter = {
                    datasets: [1, 2, 3, 4],
                    eventNo: eventConfig!.eventNo,
                    filter: emptyPathwayFilter()
                }
                eventConfig!.eventFilter = eventFilter
            }

            state.currentFilter = eventFilter
        },
        PATHWAY_UPDATE_FILTER_ON_EVENT: (state, action) => {
            const eventNo = action.eventFilter.eventNo
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            const eventConfig = findEventConfig(pathway, eventNo)
            eventConfig!.eventFilter = action.eventFilter

        },
        PATHWAY_TOGGLE_DESTINATION_METRIC: (state, action) => {
            const eventNo = action.destinationMetric.eventNo
            const dataset = action.destinationMetric.dataset
            const pathway = findPathway(state.pathways, state.pathwayId!)!
            const eventConfig = findEventConfig(pathway, eventNo)

            const index = _.indexOf(eventConfig?.datasets, dataset)
            if (index === -1) {
                // toggle on
                eventConfig?.datasets.push(dataset)
            } else {
                // toggle off
                eventConfig!.datasets = _.filter(eventConfig!.datasets, i => i !== dataset)
            }
        },
        PATHWAY_SET_PATHWAYS_FOR_CURRENT_QUERY: (state, action) => {
            state.pathways = action.pathways
        },
        PATHWAY_DELETE_PATHWAY_FROM_STATE: (state, action) => {
            const index = _.findIndex(state.pathways, { id: action.pathwayId })
            state.pathways.splice(index, 1)
        }
    }
)

export function findPathway(pathways: CohortPathway[], pathwayId: number): (CohortPathway | undefined) {
    return _.find(pathways, { id: pathwayId })
}

function findEventConfig(pathway: CohortPathway, eventNo: number): (EventConfig | undefined) {
    return _.find(pathway!.eventConfigs, i => i.eventNo === eventNo)
}

export function findDestinationFilter(eventConfig: EventConfig, dataset: number): (DestinationFilter | undefined) {
    return _.find(eventConfig!.destinationFilters, j => j.dataset === dataset)
}

export default pathway